// Cart order summary: discount code field and the shipping info tooltip.
// Both elements live inside the footer's `.js-contents`, which cart.js
// replaces after every quantity change, so they re-bind on connect.

if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.input = this.querySelector('.cart-summary__discount-input');
      this.applyButton = this.querySelector('.cart-summary__discount-apply');
      this.errorEl = this.querySelector('.cart-summary__discount-error');
      if (!this.input || !this.applyButton) return;

      this.input.addEventListener('input', () => {
        this.applyButton.disabled = this.input.value.trim() === '';
        this.showError('');
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.apply();
      });
      this.applyButton.addEventListener('click', () => this.apply());
      this.querySelectorAll('[data-remove-code]').forEach((button) => {
        button.addEventListener('click', () => {
          const remaining = CartDiscount.parseCodes(this.dataset.applied || '').filter(
            (code) => code.toLowerCase() !== button.dataset.removeCode.toLowerCase()
          );
          this.update(remaining, []);
        });
      });
    }

    static parseCodes(value) {
      const codes = [];
      value.split(',').forEach((part) => {
        const code = part.trim();
        if (code && !codes.some((seen) => seen.toLowerCase() === code.toLowerCase())) codes.push(code);
      });
      return codes;
    }

    // The field only takes new codes: applied ones live in the badges below
    // it, each removable on its own, like checkout.
    apply() {
      const typed = CartDiscount.parseCodes(this.input.value);
      if (!typed.length) return;
      const applied = CartDiscount.parseCodes(this.dataset.applied || '');
      const appliedLower = applied.map((code) => code.toLowerCase());
      const attempted = typed.filter((code) => !appliedLower.includes(code.toLowerCase()));
      if (!attempted.length) return;
      this.update(applied.concat(attempted), attempted);
    }

    sectionsToRender() {
      const sections = [];
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
      if (document.getElementById('cart-icon-bubble')) {
        sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
      }
      if (document.getElementById('cart-live-region-text')) {
        sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
      }
      if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
      return sections;
    }

    async post(codes) {
      const sections = this.sectionsToRender();
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          discount: codes.join(','),
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart update failed');
      return state;
    }

    // Shopify accepts any string and reports a code that does not apply as
    // `applicable: false` with HTTP 200, so the flag decides success. Codes
    // that do not apply are taken back out so they never reach checkout, and
    // the field keeps what the shopper typed next to the error.
    async update(codes, attempted) {
      const typed = this.input.value;
      const same = (a, b) => a.toLowerCase() === b.toLowerCase();
      this.setBusy(true);
      try {
        let state = await this.post(codes);
        const rejected = attempted.filter((code) => {
          const match = (state.discount_codes || []).find((entry) => same(entry.code, code));
          return !match || !match.applicable;
        });
        if (rejected.length) {
          state = await this.post(codes.filter((code) => !rejected.some((bad) => same(bad, code))));
        }
        const errorText = this.dataset.errorText;
        this.render(state);
        const fresh = document.querySelector('#main-cart-footer cart-discount');
        if (!fresh || fresh === this) this.setBusy(false);
        const target = fresh || this;
        if (rejected.length && target.input) {
          target.input.value = typed;
          target.applyButton.disabled = false;
          target.showError(errorText);
          target.input.focus();
        }
      } catch (error) {
        this.setBusy(false);
        this.showError(this.dataset.genericError);
      }
    }

    render(state) {
      if (!state.sections) return;
      this.sectionsToRender().forEach((section) => {
        const html = state.sections[section.section];
        if (!html) return;
        const target =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (target && source) target.innerHTML = source.innerHTML;
      });
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.input) this.input.disabled = busy;
      if (this.applyButton) this.applyButton.disabled = busy || this.input.value.trim() === '';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
      if (this.input) this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }
  }
  customElements.define('cart-discount', CartDiscount);
}

if (!customElements.get('cart-summary-tooltip')) {
  class CartSummaryTooltip extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      const button = this.querySelector('button');
      if (!button) return;
      const close = () => button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        button.setAttribute('aria-expanded', button.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      });
      button.addEventListener('blur', close);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    }
  }
  customElements.define('cart-summary-tooltip', CartSummaryTooltip);
}

// Keep the cart page current when something else changes the cart (for
// example the "Complete your setup" section). Dawn's cart.js re-renders only
// the line items for those updates, which would leave the Order summary stale.
// Both sections are fetched by their template section ids so the render keeps
// the cart template's blocks.
if (!window.cartSummaryRefreshBound && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  window.cartSummaryRefreshBound = true;
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === 'cart-items') return;
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (!footer || !footer.classList.contains('cart__footer--summary')) return;
    const hosts = [items, footer].filter((host) => host && host.dataset.id);
    fetch(`${routes.cart_url}?sections=${hosts.map((host) => host.dataset.id).join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        hosts.forEach((host) => {
          const html = sections[host.dataset.id];
          if (!html) return;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-contents');
          const target = host.querySelector('.js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        });
      })
      .catch(() => {});
  });
}

// Affirm line: [monthly] is the Estimated total divided by the block's months,
// rounded up to whole dollars. The line sits outside the re-rendered summary,
// so it follows the total whenever the summary markup is replaced.
(function () {
  const footer = document.getElementById('main-cart-footer');
  if (!footer) return;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  const format = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const sync = () => {
    const total = footer.querySelector('[data-cart-total]');
    if (!total) return;
    const cents = parseInt(total.dataset.cartTotal, 10);
    if (Number.isNaN(cents)) return;
    footer.querySelectorAll('[data-affirm-months]').forEach((line) => {
      const months = parseInt(line.dataset.affirmMonths, 10) || 12;
      const text = format.format(Math.ceil(cents / 100 / months));
      line.querySelectorAll('[data-affirm-monthly]').forEach((amount) => {
        if (amount.textContent !== text) amount.textContent = text;
      });
    });
  };
  new MutationObserver(sync).observe(footer, { childList: true, subtree: true });
})();
